import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

export const LOGO_URL =
  "https://customer-assets-wrfwihn1.emergentagent.net/job_d3fe3e7c-7f87-4c5c-846b-649ff5acb110/artifacts/196ypfub_Main%20Logo%20Design.png";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/vehicles", label: "Vehicles" },
  { to: "/calculator", label: "Calculator" },
  { to: "/pre-qualify", label: "Pre-Qualify" },
  { to: "/apply", label: "Apply" },
  { to: "/track", label: "Track" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 w-full z-50 backdrop-blur-xl border-b transition-colors duration-300 ${
        scrolled ? "bg-[#0B1F4E]/90 border-sky-300/20" : "bg-[#0B1F4E]/60 border-white/5"
      }`}
    >
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-20">
        <Link to="/" data-testid="nav-logo-link" onClick={() => setOpen(false)}>
          <span className="inline-flex items-center rounded-xl bg-white px-3 py-1.5 shadow-lg shadow-black/30">
            <img src={LOGO_URL} alt="CompCar Motors" className="h-9 w-auto" />
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-semibold tracking-wide transition-colors duration-200 ${
                  isActive ? "text-[#F57C00]" : "text-slate-300 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/pre-qualify"
            data-testid="nav-cta-button"
            className="bg-[#F57C00] hover:bg-[#E67300] text-white text-sm font-bold px-5 py-2.5 rounded-md transition-colors duration-300"
          >
            Get Pre-Qualified
          </Link>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-white p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-nav"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden overflow-hidden bg-[#0B1F4E]/95 backdrop-blur-xl border-t border-sky-300/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `text-lg font-semibold ${isActive ? "text-[#F57C00]" : "text-slate-200"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/pre-qualify"
                data-testid="mobile-nav-cta-button"
                onClick={() => setOpen(false)}
                className="bg-[#F57C00] text-white text-center font-bold px-5 py-3 rounded-md"
              >
                Get Pre-Qualified
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
